import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { finalize, shareReplay, tap } from 'rxjs/operators';
import { Question, VisibilityCondition } from '../../components/questionnaire/questionnaire';
import {
  ApiQuestion,
  PRE_QUESTIONNAIRE_PHASE,
  QuestionnaireResponse,
} from '../../models/questionnaire.model';
import { EnvConfigService } from '../env-config-service/env-config-service';
import { LocaleService } from '../locale/locale.service';

interface QuestionSection {
  position: number;
  name: string;
  sectionDescription?: string;
  questions: Question[];
}

export interface PhaseData {
  [sectionName: string]: QuestionSection;
}

export interface QuestionsStructure {
  [phase: number]: PhaseData;
}

/**
 * Loads questionnaire definitions from the API and maps them to the FE's
 * `QuestionsStructure` / `Question[]` view models.
 *
 * Loading is async (`loadQuestionnaire(slug)`); the raw `QuestionnaireResponse`
 * is cached in a signal. Read access (`getStructure`, `getVersion`) is reactive
 * and locale-aware — it reads the cache signal AND `LocaleService.locale()`, so
 * calling it inside a `computed()` re-maps automatically when the user flips
 * locale (no refetch). Danish `_da` text wins when the active locale is `da`.
 *
 * The API is the sole source of truth — a failed fetch propagates the error to
 * the caller; there is no offline/bundled fallback.
 */
@Injectable({
  providedIn: 'root',
})
export class QuestionnaireVersionService {
  private readonly http = inject(HttpClient);
  private readonly envConfigService = inject(EnvConfigService);
  private readonly localeService = inject(LocaleService);

  /** Cache of fetched responses, keyed by (slug, version). Signal so reads are reactive. */
  private readonly _cache = signal<Record<string, QuestionnaireResponse>>({});

  /** In-flight requests keyed by cache key, so concurrent loads of the same
   *  questionnaire share a single HTTP request instead of racing duplicates. */
  private readonly _inFlight = new Map<string, Observable<QuestionnaireResponse>>();

  private get baseUrl(): string {
    return this.envConfigService.config()?.orgApiBaseUrl || '';
  }

  // ---------------------------------------------------------------------------
  // Loading
  // ---------------------------------------------------------------------------

  /**
   * Fetch the questionnaire for a slug and cache it. Returns the cached value
   * immediately if already loaded. When `version` is given, requests that
   * specific version (`?version=…`) — used by the editor to render the exact
   * version an existing assessment was authored against; omit it to get the
   * current version. A failed fetch propagates the error to the caller.
   *
   * The cache is keyed by (slug, requested version), so a specific-version
   * request never collides with the current-version entry.
   */
  loadQuestionnaire(slug: string, version?: string): Observable<QuestionnaireResponse> {
    const key = this.cacheKey(slug, version);
    const cached = this._cache()[key];
    if (cached) {
      return of(cached);
    }

    // Share an already-running request so duplicate concurrent loads (e.g. the
    // editor populating the form twice before the first response lands) don't
    // fire a second GET.
    const inFlight = this._inFlight.get(key);
    if (inFlight) {
      return inFlight;
    }

    const query = version ? `?version=${encodeURIComponent(version)}` : '';
    const url = `${this.baseUrl}/questionnaires/${encodeURIComponent(slug)}${query}`;
    const request$ = this.http.get<QuestionnaireResponse>(url).pipe(
      tap(response => this._cache.update(cache => ({ ...cache, [key]: response }))),
      finalize(() => this._inFlight.delete(key)),
      shareReplay(1)
    );
    this._inFlight.set(key, request$);
    return request$;
  }

  // ---------------------------------------------------------------------------
  // Reactive, locale-aware reads
  // ---------------------------------------------------------------------------

  /**
   * The mapped questions structure for a (slug, version), or `null` if not yet
   * loaded. Pass the same `version` used in `loadQuestionnaire`. Reactive:
   * re-maps when the cache or the active locale changes.
   *
   * When `localeOverride` is supplied (e.g. `'da'` or `'en'`), the structure
   * uses that locale for question/option text instead of the app-wide locale.
   * This lets the assessment editor render the questionnaire in the language
   * the user chose at creation time, independent of the platform language.
   */
  getStructure(slug: string, version?: string, localeOverride?: string): QuestionsStructure | null {
    const response = this._cache()[this.cacheKey(slug, version)];
    if (!response) {
      return null;
    }
    const useDa = localeOverride ? localeOverride === 'da' : this.localeService.locale() === 'da';
    return this.responseToStructure(response, useDa);
  }

  /** Cache key: scoped to the requested version so specific + current entries coexist. */
  private cacheKey(slug: string, version?: string): string {
    return version ? `${slug}@${version}` : slug;
  }

  /**
   * Flatten a structure into an ordered `Question[]` (phase asc, then
   * section position asc).
   *
   * **Phase 0 is excluded by default.** It holds the pre-questionnaire
   * question(s) the create wizard renders itself — leaving it in would
   * put it back as page 1 of the paged questionnaire, defeating the
   * point of asking it before the AI suggests everything else
   * (`dpia-v2-fe-changes.md` §1). Pass `includePreQuestionnaire: true`
   * for consumers that need the complete answer set (e.g. building the
   * chat's questionnaire-context blob).
   */
  getQuestionsArray(
    structure: QuestionsStructure,
    opts?: { includePreQuestionnaire?: boolean }
  ): Question[] {
    const includePre = opts?.includePreQuestionnaire === true;
    const questions: Question[] = [];
    const phases = Object.keys(structure)
      .map(Number)
      .filter(phase => includePre || phase !== PRE_QUESTIONNAIRE_PHASE)
      .sort((a, b) => a - b);

    for (const phase of phases) {
      const phaseData = structure[phase];
      const sections = Object.values(phaseData).sort((a, b) => a.position - b.position);

      for (const section of sections) {
        questions.push(...section.questions);
      }
    }

    return questions;
  }

  /**
   * The pre-questionnaire questions (phase 0) only, in section order.
   * Empty for questionnaires without a phase 0 (`ai_risk`).
   *
   * Rendered by the create-assessment wizard, and echoed read-only on
   * the questionnaire's review page. Reading the same rows the API
   * serves keeps wording, help text and Danish translations
   * single-sourced — never hard-code the question text at a call site.
   */
  getPreQuestionnaireQuestions(structure: QuestionsStructure): Question[] {
    const phaseData = structure[PRE_QUESTIONNAIRE_PHASE];
    if (!phaseData) return [];
    return Object.values(phaseData)
      .sort((a, b) => a.position - b.position)
      .flatMap(section => section.questions);
  }

  // ---------------------------------------------------------------------------
  // Mapping: QuestionnaireResponse -> QuestionsStructure
  // ---------------------------------------------------------------------------

  private responseToStructure(response: QuestionnaireResponse, da: boolean): QuestionsStructure {
    const result: QuestionsStructure = {};

    for (const phase of response.phases) {
      const phaseData: PhaseData = {};

      phase.sections.forEach((section, index) => {
        const sectionName = this.localized(section.name, section.name_da, da);
        const sectionDescription =
          this.localized(section.description, section.description_da, da) || undefined;

        phaseData[sectionName] = {
          position: index + 1,
          name: sectionName,
          sectionDescription,
          questions: section.questions.map(q =>
            this.mapQuestion(q, phase.phase, sectionName, sectionDescription, da)
          ),
        };
      });

      result[phase.phase] = phaseData;
    }

    return result;
  }

  private mapQuestion(
    q: ApiQuestion,
    phase: number,
    sectionName: string,
    sectionDescription: string | undefined,
    da: boolean
  ): Question {
    const options = (da && q.options_da) || q.options || undefined;
    const visibleIf: VisibilityCondition | undefined = q.visible_if
      ? {
          questionId: q.visible_if.question_key,
          operator: q.visible_if.operator,
          value: q.visible_if.value,
        }
      : undefined;
    return {
      id: q.key,
      type: q.type as Question['type'],
      question: this.localized(q.question, q.question_da, da),
      helpText: this.localized(q.help_text, q.help_text_da, da) || undefined,
      // Alternative help text for when no AI suggestion is coming — the
      // default copy opens with a "the agent will pre-populate this"
      // promise the system can't keep without grounding material. Only
      // the caller knows whether a suggestion is on its way, so both
      // variants are mapped and the choice is made at the call site.
      helpTextUnassisted:
        this.localized(q.help_text_unassisted, q.help_text_unassisted_da, da) || undefined,
      options: options ?? undefined,
      hasOther: q.has_other,
      phase,
      section: sectionName,
      sectionDescription,
      visibleIf,
      // Pre-V044 rows have no `required` column; every one of them was
      // mandatory, so absent must mean `true` or `ai_risk` behaviour
      // changes silently.
      required: q.required ?? true,
    };
  }

  /** Pick the Danish variant when the locale is `da` and a value exists, else the default. */
  private localized(
    value: string | null | undefined,
    valueDa: string | null | undefined,
    da: boolean
  ): string {
    return ((da && valueDa) || value) ?? '';
  }
}
